import { createSlice } from '@reduxjs/toolkit';
import { activityInitialState, activityReducers } from '../_shared/activityState';

/**
 * AssetGroups slice — scoped by org + site.
 *
 * Data structure:
 *   state.data[orgID][siteID] = { data: [...], loading: false }
 *
 * Three list-load actions prevent saga loops:
 *   loadGroups       → trigger (saga listens, reducer is no-op)
 *   loadGroupsStart  → sets bucket loading (no saga listens)
 *   loadGroupsSuccess → replaces bucket data
 */

const initialState = {
	activity: { ...activityInitialState },
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	// Group-nesting structure (site-invariant, org-scoped): structure[orgID] = { byGroupID, loading, error }
	// byGroupID[groupID] = { groupID, name, externalType, directDeviceIDs:[], childGroupIDs:[] }
	structure: {},
	error: null,
	searchable: true,

	// ✅ Loading States
	loadingAddGroup: false,
	loadingDeleteGroup: false,
	loadingAddGroupAsset: false,
	loadingDeleteGroupAsset: false,

	updateGroupLoading: false,
	deleteGroupLoading: false,

	groupAssetsLoading: false,
	addGroupAssetLoading: false,
	removeGroupAssetLoading: false,

	// Group nesting (child links)
	loadingLinkChildGroups: false,
	loadingUnlinkChildGroups: false,
	loadingChildGroupSchedule: false
};

// Ensures the bucket exists at data[orgID][key]
const ensureBucket = (state, orgID, siteID) => {
	const key = siteID;
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) state.data[orgID][key] = { data: [], loading: false };
	return key;
};

// Patches a single group inside the active scope bucket
const patchEntity = (state, orgID, siteID, groupID, patcher) => {
	const key = siteID;
	const bucket = state.data[orgID]?.[key];
	if (!bucket?.data) return;
	bucket.data = bucket.data.map((g) => (g.groupID === groupID ? patcher(g) : g));
};

const assetGroupSlice = createSlice({
	name: 'assetGroup',
	initialState,
	reducers: {
		...activityReducers,
		// ── List load (scoped) ──────────────────────────────────────────

		// Trigger — saga listens (takeLatest). Reducer is a no-op.
		loadGroups: () => {},

		// Sets loading on the target bucket. No saga listens (prevents loop).
		loadGroupsStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},

		// Replaces bucket data — backend is source of truth for this scope.
		loadGroupsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key] = { data, loading: false };
		},

		// Stops loading on the bucket, sets global error.
		loadGroupsError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID;
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.error = error;
		},

		// REFRESH (silent — replaces data without loading flash, used after inline edits)
		refreshGroups: () => {},
		refreshGroupsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			const existing = state.data[orgID][key].data || [];

			// Preserve devices sub-entity from existing groups
			const devicesMap = {};
			existing.forEach((g) => {
				if (g.devices) devicesMap[g.groupID] = g.devices;
			});

			state.data[orgID][key].data = data.map((g) => ({
				...g,
				...(devicesMap[g.groupID] ? { devices: devicesMap[g.groupID] } : {})
			}));
		},

		// ── Sub-entity patchers ─────────────────────────────────────────

		loadAssetsGroups(state) {
			state.error = null;
		},
		loadAssetsGroupsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = siteID;
			const bucket = state.data[orgID]?.[key];
			if (!bucket?.data) return;

			// data is an object with groupID as key and devices as value array of devices
			// Merge the devices with the groups
			bucket.data = bucket.data.map((group) => {
				const devices = data[group.groupID] || [];
				return { ...group, devices };
			});
		},
		loadAssetsGroupsError(state, action) {
			state.error = action.payload;
		},

		// ── Group-nesting structure (org-scoped, site-invariant) ────────

		// Trigger — saga listens (takeLatest). Reducer is a no-op.
		loadGroupStructure: () => {},
		loadGroupStructureStart(state, action) {
			const { orgID } = action.payload;
			if (!state.structure[orgID]) state.structure[orgID] = { byGroupID: {}, loading: false, error: null };
			state.structure[orgID].loading = true;
			state.structure[orgID].error = null;
		},
		loadGroupStructureSuccess(state, action) {
			const { orgID, byGroupID } = action.payload;
			state.structure[orgID] = { byGroupID, loading: false, error: null };
		},
		loadGroupStructureError(state, action) {
			const { orgID, error } = action.payload;
			if (!state.structure[orgID]) state.structure[orgID] = { byGroupID: {}, loading: false, error: null };
			state.structure[orgID].loading = false;
			state.structure[orgID].error = error;
		},

		// ── CRUD actions (global loading flags) ─────────────────────────

		// Add Group
		// Add group — the modal closes optimistically on dispatch; this flag only drives
		// the topbar in-flight indicator, so no success flag is needed.
		addGroup(state) {
			state.loadingAddGroup = true;
			state.error = null;
		},
		addGroupSuccess(state) {
			state.loadingAddGroup = false;
		},
		addGroupError(state, action) {
			state.error = action.payload;
			state.loadingAddGroup = false;
		},

		// Update Group
		updateGroup(state, action) {
			state.updateGroupLoading = true;
			state.error = null;
			// Optimistic update: patch the group in all buckets immediately
			const { groupID, data } = action.payload || {};
			if (groupID && data) {
				Object.keys(state.data).forEach((orgID) => {
					Object.keys(state.data[orgID]).forEach((siteKey) => {
						const bucket = state.data[orgID][siteKey];
						if (bucket?.data) {
							bucket.data = bucket.data.map((g) =>
								g.groupID === groupID || g.assetGroupID === groupID ? { ...g, ...data } : g
							);
						}
					});
				});
			}
		},
		updateGroupSuccess(state) {
			state.updateGroupLoading = false;
		},
		updateGroupError(state, action) {
			state.error = action.payload;
			state.updateGroupLoading = false;
		},

		// Delete Group
		// Delete group — closes optimistically; flag only drives the topbar indicator.
		deleteGroup(state) {
			state.loadingDeleteGroup = true;
			state.error = null;
		},
		deleteGroupSuccess(state) {
			state.loadingDeleteGroup = false;
		},
		deleteGroupError(state, action) {
			state.error = action.payload;
			state.loadingDeleteGroup = false;
		},

		// Load Group Assets
		loadGroupAssets(state) {
			state.groupAssetsLoading = true;
			state.error = null;
		},
		loadGroupAssetsSuccess(state, action) {
			const { orgID, siteID, groupID, data } = action.payload;
			patchEntity(state, orgID, siteID, groupID, (g) => ({ ...g, devices: data }));
			state.groupAssetsLoading = false;
		},
		loadGroupAssetsError(state, action) {
			state.error = action.payload;
			state.groupAssetsLoading = false;
		},

		// Silent refresh — same as loadGroupAssets but without loading flag
		refreshGroupAssets: () => {},
		refreshGroupAssetsSuccess(state, action) {
			const { orgID, siteID, groupID, data } = action.payload;
			patchEntity(state, orgID, siteID, groupID, (g) => ({ ...g, devices: data }));
		},

		// Add Group assets — modal closes optimistically; loading drives the topbar.
		addGroupAsset(state) {
			state.loadingAddGroupAsset = true;
			state.error = null;
		},
		addGroupAssetSuccess(state) {
			state.loadingAddGroupAsset = false;
		},
		addGroupAssetError(state, action) {
			state.error = action.payload;
			state.loadingAddGroupAsset = false;
		},

		// Delete Group assets — modal closes optimistically; loading drives the topbar.
		removeGroupAsset(state) {
			state.loadingDeleteGroupAsset = true;
		},
		removeGroupAssetSuccess(state) {
			state.loadingDeleteGroupAsset = false;
		},
		removeGroupAssetError(state, action) {
			state.error = action.payload;
			state.loadingDeleteGroupAsset = false;
		},

		// ── Group nesting: link/unlink child groups ─────────────────────

		linkChildGroups(state) {
			state.loadingLinkChildGroups = true;
			state.error = null;
		},
		linkChildGroupsSuccess(state) {
			state.loadingLinkChildGroups = false;
		},
		linkChildGroupsError(state, action) {
			state.error = action.payload;
			state.loadingLinkChildGroups = false;
		},

		unlinkChildGroups(state) {
			state.loadingUnlinkChildGroups = true;
			state.error = null;
		},
		unlinkChildGroupsSuccess(state) {
			state.loadingUnlinkChildGroups = false;
		},
		unlinkChildGroupsError(state, action) {
			state.error = action.payload;
			state.loadingUnlinkChildGroups = false;
		},

		// ── PermissionGroup→List edge schedule (unified-schedule-expiry, IR-only) ──
		updateChildGroupSchedule(state) {
			state.loadingChildGroupSchedule = true;
			state.error = null;
		},
		updateChildGroupScheduleSuccess(state) {
			state.loadingChildGroupSchedule = false;
		},
		updateChildGroupScheduleError(state, action) {
			state.error = action.payload;
			state.loadingChildGroupSchedule = false;
		},

		// ── Combined List link/unlink + per-List schedule (IR "Manage lists" modal) ──
		// One sequential saga: unlink → link → set each edge schedule (a new List's schedule must run
		// AFTER its link exists), so staged adds can carry a chosen schedule.
		updateGroupLists(state) {
			state.loadingLinkChildGroups = true;
			state.error = null;
		},
		updateGroupListsSuccess(state) {
			state.loadingLinkChildGroups = false;
		},
		updateGroupListsError(state, action) {
			state.error = action.payload;
			state.loadingLinkChildGroups = false;
		}

		// Add more actions here
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState; // Reset state on logout
		});
	}
});

// Exporting the actions and reducer
export const assetGroupActions = assetGroupSlice.actions;

export default assetGroupSlice.reducer;
